import type { DatePartsMap } from '@noths/polaris-client-utils';
import { formatDate, getOrdinalSuffix } from '@noths/polaris-client-utils';

/**
 * Formats an estimated delivery date as a short weekday and an ordinal day, for
 * example "Wed 22nd".
 *
 * The card takes a date rather than pre-formatted copy so that every surface
 * shows the same format and the ordinal rule lives in one place. Returns
 * undefined for a date that is not usable, so the caller can drop the line
 * rather than render "Invalid Date".
 *
 * Built from `formatDate`'s parts rather than its formatted string because the
 * shared formatter always includes the month and year, and this line wants
 * neither.
 */
export const formatDeliveryDate = (date: Date | string): string | undefined => {
  const parsed = typeof date === 'string' ? new Date(date) : date;

  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  const { day, weekday } = formatDate(parsed, {
    formatToParts: true,
    weekday: 'short',
  }) as DatePartsMap;

  const dayNumber = Number(day.value);

  return `${weekday?.value} ${dayNumber}${getOrdinalSuffix(dayNumber)}`;
};
